import { useState } from "react";
import { MessageCircle, Loader2, Send, Heart } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useCommentaires, useCreateCommentaire } from "@/hooks/useCommentaires";
import { useLikeStatut, useToggleLike } from "@/hooks/useLikes";
import type { CommentaireDto } from "@/types/commentaire.types";

function LikeButton({ articleId }: { articleId: number }) {
  const { t } = useI18n();
  const { data } = useLikeStatut(articleId);
  const toggleLike = useToggleLike(articleId);

  return (
    <button
      type="button"
      onClick={() => toggleLike.mutate()}
      disabled={toggleLike.isPending}
      className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
        data?.liked
          ? "border-alert/40 bg-alert/10 text-alert"
          : "border-border text-muted-foreground hover:border-alert/40 hover:text-alert"
      }`}
    >
      <Heart className="h-3.5 w-3.5" fill={data?.liked ? "currentColor" : "none"} />
      {data?.total ?? 0} {t("comments.likes")}
    </button>
  );
}

function fmtDate(iso: string, locale: string) {
  return new Date(iso).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function CommentsSection({ articleId }: { articleId: number }) {
  const { t, locale } = useI18n();
  const { data, isLoading } = useCommentaires(articleId);
  const createCommentaire = useCreateCommentaire(articleId);
  const comments: CommentaireDto[] = Array.isArray(data) ? data : [];

  const [auteurNom, setAuteurNom] = useState("");
  const [contenu, setContenu] = useState("");
  const [siteWeb, setSiteWeb] = useState(""); // honeypot — doit rester vide
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!auteurNom.trim() || !contenu.trim()) {
      setError(t("comments.form.required"));
      return;
    }
    setError("");
    createCommentaire.mutate(
      { articleId, auteurNom: auteurNom.trim(), contenu: contenu.trim(), siteWeb },
      {
        onSuccess: () => {
          setContenu("");
          setSent(true);
        },
        onError: (e: any) => {
          setError(e?.response?.data?.message || t("comments.form.error"));
        },
      }
    );
  };

  return (
    <section className="mt-16 border-t border-border pt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold tracking-tight">
          <MessageCircle className="h-5 w-5 text-verified" />
          {t("comments.title")} {comments.length > 0 && `(${comments.length})`}
        </h2>
        <LikeButton articleId={articleId} />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-3 rounded-md border border-border bg-surface/40 p-4">
        {/* Honeypot — invisible pour un humain, souvent rempli par un bot */}
        <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
          <label htmlFor="site-web">Site web</label>
          <input
            id="site-web"
            name="siteWeb"
            tabIndex={-1}
            autoComplete="off"
            value={siteWeb}
            onChange={(e) => setSiteWeb(e.target.value)}
          />
        </div>

        <input
          value={auteurNom}
          onChange={(e) => setAuteurNom(e.target.value)}
          placeholder={t("comments.form.name")}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <textarea
          value={contenu}
          onChange={(e) => setContenu(e.target.value)}
          placeholder={t("comments.form.placeholder")}
          rows={3}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
        />
        {error && <p className="text-xs text-alert">{error}</p>}
        {sent && !error && <p className="text-xs text-verified">{t("comments.form.sent")}</p>}
        <button
          type="submit"
          disabled={createCommentaire.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-xs font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors"
        >
          {createCommentaire.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {t("comments.form.submit")}
        </button>
      </form>

      <div className="mt-8 space-y-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("comments.empty")}</p>
        ) : (
          comments.map((c) => (
            <div key={c.id} className="border-b border-border pb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{c.auteurNom}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(c.creeLe, locale)}</span>
              </div>
              <p className="mt-1.5 text-sm text-foreground/90 whitespace-pre-wrap">{c.contenu}</p>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
